import React, { useEffect, useMemo, useRef, useState } from "react";
import { ManualStockAdjustmentRowsData, StatusFilterMenuItems } from "../../../types/types";
import { Loader } from "../../../components/loader/Loader";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import Header from "../../../components/dms_common/Header";
import {
  defaultSearchFunction,
  filterData,
  getDatesFromLocalStorage,
  updateDatesInLocalStorage,
} from "../../../components/dms_common/DmsCommonServices";
import {
  approvalStatusMapping,
  manualStockAdjustmentMetadata,
  setManualAdjustStockDataInDb,
} from "./ManualStockAdjustmentUtils";
import { getMergedCatalogueAndBatchIdData, setBatchWiseStockDataInDb } from "../batchAndExpiryManagement/BatchAndExpiryManagementUtils";
import AdjustStockLevelsForm from "./AdjustStockLevelsForm";
import { Alert, Button, Snackbar, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getUTCDate } from "../../../services/manageOrderService";
import { downloadAllReports } from "../../../services/manageReportService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { getConfigData } from "../../../services/configurationService";
import { Product } from "../../createSecondaryOrder/models";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";
import { isGapMoreThanNDays } from "../../../utils/calenderUtils";
import { getDistributorList } from "../RealTimeStock/RealTimeStockUtils";
import RealTimeStockStatusFilter from "../RealTimeStock/RealTimeStockStatusFilter";
import realTimeEmptyImage from "../../../assets/image/svg/realTimeEmptyImage.svg";
import EmptyPage from "../../claimsNew/components/SelectClaimType";

function ManualStockAdjustment({ isCompanyUser }: { isCompanyUser: boolean }) {
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [isLoading, setIsLoading] = useState(true);
  const [openStockAdjustForm, setOpenCreateBatchIdForm] = useState(false);
  const [rows, setRows] = useState<ManualStockAdjustmentRowsData[]>([]);
  const [data, setData] = useState<ManualStockAdjustmentRowsData[]>([]);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [skuProductDescriptionMap, setSkuProductDescriptionMap] = useState<Record<string, string>>({});
  const [skuUomMap, setSkuUomMap] = useState<Record<string, string>>({});
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const stockAdjustmentReportRef = useRef<string | null>(null);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(initialGenericModalProps);
  const [resetSearch, setResetSearch] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState<string>("");
  const [distributors, setDistributors] = useState<StatusFilterMenuItems[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [distributorsLoaded, setDistributorsLoaded] = useState(false);
  
  const feature = "Stock";
  const pageID = "ManualStockAdjust";

  useEffect(() => {
    if (!isCompanyUser) {
      // For non-company users, only check if data is loaded
      setIsLoading(!dataLoaded);
    } else {
      // For company users, check both distributors and data are loaded
      // Also check if a distributor is selected before showing data
      if (distributorsLoaded && (!selectedDistributor || dataLoaded)) {
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }
    }
  }, [isCompanyUser, dataLoaded, distributorsLoaded, selectedDistributor]);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const isStartDateIsGreaterThanEndDate = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    const diff = endDate.diff(startDate);
    return diff < 0;
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      setGenericModalProps(getGenericModalProps(`positive`, `You can't select date range more than ${validDateRange} days!`));
      return false;
    }
    if (startDate && endDate && isStartDateIsGreaterThanEndDate(startDate, endDate)) {
      setGenericModalProps(getGenericModalProps(`positive`, `To Date should be greater than from date.`));
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const handleApply = async () => {
    if (canWeMakeRequest()) {
      setDataLoaded(false);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllStockData(start, end);
    }
  };

  const handleClearAll = async() => {
    setDataLoaded(false);
    const date = dayjs();
    const start = getUTCDate(date, "start");
    const end = getUTCDate(date, "end");
    setStartDate(dayjs());
    setEndDate(dayjs());
    updateDatesInLocalStorage(date, date, feature, pageID);
    await getAllStockData(start, end);
  };

  // Data processing functions
  const processStockData = async(responseData: any[], productsMap: Record<string, Product>) => {
    const skuProductMap: Record<string, string> = {};
    const tempSkuUomMap: Record<string, string> = {};
    const stockSkuCodes = new Set<string>();
    const stockData =await getDataFromLocalDb("BatchWiseStockData");

    stockData?.data?.forEach((item: any) => {
      if (item.skuCode) {
        const hasAvailableQty = 
          (item.pieceQty?.availableQty && item.pieceQty.availableQty > 0) ||
          (item.caseQty?.availableQty && item.caseQty.availableQty > 0) ||
          (item.otherQty?.availableQty && item.otherQty.availableQty > 0);
        
        if (hasAvailableQty) {
          stockSkuCodes.add(item.skuCode);
        }
      }
    });
    
    Object.values(productsMap).forEach((item) => {
       if (stockSkuCodes.has(item.batchCode)) {
      skuProductMap[item.batchCode] = item.skuDescription;
      tempSkuUomMap[item.batchCode] = ConfigDictionary.orderUomCheck === "true" ? (item?.uom ?? "") : "all";
       }
    });
    
    setSkuProductDescriptionMap(skuProductMap);
    setSkuUomMap(tempSkuUomMap);
    
    return getMergedCatalogueAndBatchIdData(responseData, productsMap).map((item: any): ManualStockAdjustmentRowsData => ({
      id: item.requestId,
      requestId: item.requestId,
      skuCode: item.skuCode,
      stockType: item.stockType,
      currentPieceStockQty: item.currentPieceStockQty,
      updatedPieceStockQty: item.updatedPieceStockQty,
      currentCaseStockQty: item.currentCaseStockQty,
      updatedCaseStockQty: item.updatedCaseStockQty,
      currentOtherStockQty: item.currentOtherStockQty,
      updatedOtherStockQty: item.updatedOtherStockQty,
      adjustmentDate: item.adjustmentDate,
      status: item.overallStatus,
      skuDetails: item.skuDetails,
      expiryDate: item.expiryDate ?? "",
      skuDescription: item.skuDescription ?? "",
      batchIdCount: item?.skuDetails?.length ?? 0,
      otherUnitName : item?.otherUnitName,
    }));
  };

  const getAllStockData = async (startDate: string, endDate: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true);
    setTimeout(() => setResetSearch(false), 0);

    try {
      await setManualAdjustStockDataInDb(startDate, endDate, isCompanyUser ? selectedDistributor : "");
      const response = await getDataFromLocalDb("ManualStockAdjustData");
      const productsData = await getDataFromLocalDb("Product");
      const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
      const mergedData = await processStockData(response.data, productsMap);
      setRows(mergedData);
      setData(mergedData);
      setDataLoaded(true);
      
      logAnalyticsEvent("manual_stock_adjustment_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_skus_displayed": mergedData.length
      });
    } catch (error) {
      setRows([]);
      setData([]);
      setDataLoaded(true);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching manual stock adjustment data`));
      logAnalyticsEvent("manual_stock_adjustment_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_skus_displayed": 0
      });
    }
  };

  const init = async () => {
    setDataLoaded(false);
    setTimeout(() => setResetSearch(false), 0);
    const distributorId = !isCompanyUser ? JSON.parse(localStorage.authContext).user.loginId : null;
    
    try {
      const { startDate, endDate } = getDatesFromLocalStorage(feature, pageID, 7);
      setStartDate(startDate);
      setEndDate(endDate);
      await setBatchWiseStockDataInDb(distributorId ?? (isCompanyUser ? selectedDistributor : ""));
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");

      await setManualAdjustStockDataInDb(start, end, distributorId ?? (isCompanyUser ? selectedDistributor : ""));
      const response = await getDataFromLocalDb("ManualStockAdjustData");
      const productsData = await getDataFromLocalDb("Product");
      const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
      
      const mergedData = await processStockData(response.data, productsMap);
      setRows(mergedData);
      setData(mergedData);

      const stockAdjustmentReportIdConfig = await getConfigData("dms_configuration", "stockAdjustmentReportId");
      stockAdjustmentReportRef.current = stockAdjustmentReportIdConfig[0]?.configValue ?? "";
      setLastRefreshed(dayjs());
      setDataLoaded(true);
    } catch (e) {
      console.error(e);
      setDataLoaded(true);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching manual stock adjustment data`));
    }
  };

  const fetchDistributors = async () => {
    setDistributorsLoaded(false);
    try {
      const distributorsList = await getDistributorList();
      setDistributors(distributorsList);
      setDistributorsLoaded(true);
    } catch (error) {
      console.error("Error fetching distributors list:", error);
      setDistributorsLoaded(true);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching distributors list`));
    }
  };

  useEffect(() => {
    if (selectedDistributor) {
      setDataLoaded(false);
      logLoadTrace(init, "real_time_stock_distributor_change_time");
    }
  }, [selectedDistributor]);

  const handleDistributorChange = (distributorId: string) => {
    setSelectedDistributor(distributorId);
  };

  const handleRefresh = async () => {
    setDataLoaded(false);
    try {
      await init();
    } catch (error) {
      console.error(error);
      setDataLoaded(true);
    }
  };

  // Effects
  const filteredData = useMemo(() => {
    return filterData(data, searchedValue.toLowerCase(), defaultSearchFunction);
  }, [searchedValue, data]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);

  useEffect(() => {
    if (isCompanyUser) {
      fetchDistributors();
    } else {
      // For non-company users, init immediately
      logLoadTrace(init, "manual_stock_adjustment_page_load_time");
    }
  }, []);

  useEffect(() => {
    setSearchedValue("");
    if (!openStockAdjustForm) {
      if (!isCompanyUser) {
        setDataLoaded(false);
        logLoadTrace(init, "manual_stock_adjustment_page_load_time");
      } else {
        setSelectedDistributor("");
        setRows([]);
        setData([]);
        setDataLoaded(false);
      }
    }
  }, [openStockAdjustForm]);

  useEffect(() => {
    const { startDate, endDate } = getDatesFromLocalStorage(feature, pageID, 7);
    const start = getUTCDate(startDate, "start");
    const end = getUTCDate(endDate, "end");
    
    const interval = setInterval(() => {
      if (!isCompanyUser || selectedDistributor) {
        getAllStockData(start, end);
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [selectedDistributor, isCompanyUser]);

  return (
    <div className="BatchAndExpiryManagement" style={{ paddingBottom: "6px" }}>
      <ScreenOverlay apply={isLoading} />
      <CustomGenericModal
          type={genericModalProps?.type}
          message={genericModalProps?.message}
          modalOpen={genericModalProps?.open}
          onClose={() => setGenericModalProps(initialGenericModalProps)}
        />
      {openStockAdjustForm ? (
        <AdjustStockLevelsForm
          isCompanyUser={isCompanyUser}
          selectedDistributor={isCompanyUser ? selectedDistributor: null}
          setOpenAdjustStockLevelsForm={setOpenCreateBatchIdForm}
          skuProductDescriptionMap={skuProductDescriptionMap}
          skuUomMap={skuUomMap}
        />
      ) : (
        <>
          <Header
            title={"Manual Stock Adjustment"}
            searchFunction={handleSearchValueChange}
            createBtnLabel={"Adjust Stock Levels"}
            handleCreateBtnClick={() => setOpenCreateBatchIdForm(true)}
            createBtnDisabled={isCompanyUser && (!selectedDistributor || selectedDistributor === "")}
            downloadReportIcon={true}
            downloadReportOnClick={() =>{
              logAnalyticsEvent( "manual_stock_report_downloaded", {
                "distributor_id": JSON.parse(localStorage.authContext).user.loginId
              });
              downloadAllReports(
                stockAdjustmentReportRef.current,
                setSnackbarOpen,
                startDate,
                endDate,
                isCompanyUser ? "loggedInUser" : undefined,
                selectedDistributor,
                selectedDistributor
              )
            }}

            enableTruncation={true}
            lastRefreshed={lastRefreshed}
            resetSearch={resetSearch}
            onRefresh={handleRefresh}
          />
          <div className="dms_toolbar">
            <div className="manual-stock-filter-main">
              <div className="stocks_support_utility_dates">
                <LocalizationProvider dateAdapter={AdapterDayjs} 
                  className={
                    isCompanyUser && selectedDistributor === ""
                      ? "manage_order_dateEnter disabled-date-picker"
                      : "manage_order_dateEnter"
                  }>
                  <DatePicker
                    label="From Date"
                    inputFormat="DD-MM-YYYY"
                    value={startDate}
                    maxDate={dayjs()}
                    onChange={(value) => {
                      if (value) {
                        setStartDate(value);
                      }
                    }}
                    renderInput={(props) => <TextField {...props} disabled = {isCompanyUser && selectedDistributor === ""} size="small" />}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    inputFormat="DD-MM-YYYY"
                    value={endDate}
                    maxDate={dayjs()}
                    onChange={(value) => {
                      if (value) {
                        setEndDate(value);
                      }
                    }}
                    renderInput={(props) => <TextField {...props} disabled = {isCompanyUser && selectedDistributor === ""} size="small" />}
                  />
                </LocalizationProvider>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#237C9D",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                    
                  }}
                  onClick={handleApply}
                  disabled={isCompanyUser && selectedDistributor===""}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#f0fbff",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                    
                  }}
                  onClick={handleClearAll}
                  disabled={isCompanyUser && selectedDistributor===""}
                >
                  Reset
                </Button>
              </div>
              {isCompanyUser && (
                <div className="Header_distributor_filter" style={{ marginRight: '15px' }}>
                  <RealTimeStockStatusFilter
                    onStatusChange={handleDistributorChange}
                    statusFilterMenuItems={distributors}
                    enableTruncation={true}
                  />
                </div>
              )}
            </div>
          </div>
          {isLoading ? (
            <Loader sx={{ position: "absolute", top: "45%" }} />
          ) : (
            isCompanyUser && selectedDistributor === "" ?( 
              <>
                <EmptyPage
                  height={87}
                  text={
                    <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                      {"Please select a Distributor from the drop-down to proceed."}
                    </div>
                  }
                  imgSrc={realTimeEmptyImage}
                  />
              </>
            ):(
              rows.length === 0 && searchedValue.length === 0 ?(
                <>
                  <EmptyPage
                    height={87}
                    text={
                      <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                        {isCompanyUser 
                          ? "No Stock Adjustment Data Available for Selected Distributor." 
                          : "No Stock Adjustment Data Available."}
                      </div>
                    }
                    imgSrc={realTimeEmptyImage}
                  />
                </>
              ):(
                <CustomDataGrid
                  searchedValue={searchedValue}
                  rows={rows}
                  columnsMetaData={manualStockAdjustmentMetadata}
                  orderStatusConfig={approvalStatusMapping}
                  redirectUrl={"/stocks/manualStockUpdate/StockAdjustSkuDetailsScreen"}
                  tableWidth={0.765}
                  noDataMessage="No Stock data found."
                />
              )
            )
          )}
        </>
      )}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManualStockAdjustment;